import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Download, History, Refresh } from '@mui/icons-material';
import { dataverseService } from '../services/dataverseService';
import { listUnlocAccessEvents } from '../services/unlocAccessLogService';
import { getDataverseErrorMessage } from '../lib/dataverseErrorHandler';
import { normalizeMobileNumber } from '../config/keyRequest';
import {
  LOG_SOURCE,
  OC_LOG_SOURCE,
  attachSessionUsers,
  buildPhoneSessions,
  collectLockFilterAliases,
  enrichLogsWithLocks,
  enrichLogsWithPhones,
  filterAccessLogs,
  intersectLockFilterAliases,
  isGuiPlaceholderUserId,
  linkOnDemandEpisodes,
  logActionLabel,
  logSourceLabel,
  mergeAccessLogRows,
  resolveLockFilterAliases,
  sessionUserLabel
} from '../lib/accessLog';
import { downloadWorkbook } from '../lib/exportExcel';
import { accessGroupService } from '../services/dataverseService';

function defaultFromIso() {
  const from = new Date();
  from.setHours(0, 0, 0, 0);
  return from.toISOString();
}

function defaultToIso() {
  const to = new Date();
  to.setHours(23, 59, 59, 999);
  return to.toISOString();
}

function toDatetimeLocalValue(iso) {
  if (!iso) {
    return '';
  }
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  const pad = (value) => String(value).padStart(2, '0');
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
}

function fromDatetimeLocalValue(value) {
  if (!value) {
    return '';
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString();
}

function formatDateTime(value) {
  if (!value) {
    return '—';
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleString('nb-NO');
}

export default function LogsPage({
  autoLoad = true,
  runId,
  hasRun = true,
  hideChrome = false
} = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(Boolean(autoLoad));
  const [error, setError] = useState(null);
  const [unlocWarning, setUnlocWarning] = useState(null);
  const [ondemandWarning, setOndemandWarning] = useState(null);
  const [rows, setRows] = useState([]);
  const [locks, setLocks] = useState([]);
  const [phones, setPhones] = useState([]);
  const [accessGroups, setAccessGroups] = useState([]);
  const [selectedAccessGroupDetail, setSelectedAccessGroupDetail] = useState(null);
  const [accessGroupLoading, setAccessGroupLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [fetchStats, setFetchStats] = useState({ unloc: 0, ondemand: 0 });

  const lockId = searchParams.get('lockId') || '';
  const phone = searchParams.get('phone') || '';
  const user = searchParams.get('user') || '';
  const source = searchParams.get('source') || 'all';
  const from = searchParams.get('from') || defaultFromIso();
  const to = searchParams.get('to') || defaultToIso();
  const accessGroupId = searchParams.get('accessGroup') || '';

  const lockAliases = useMemo(
    () => resolveLockFilterAliases(lockId, locks),
    [lockId, locks]
  );

  const groupLockAliases = useMemo(() => {
    if (!accessGroupId || !selectedAccessGroupDetail) {
      return [];
    }
    return collectLockFilterAliases(selectedAccessGroupDetail.locks || [], locks);
  }, [accessGroupId, selectedAccessGroupDetail, locks]);

  /** Lock ids used for client filter + Dataverse query (group ∩ optional single lock). */
  const effectiveLockFilterIds = useMemo(() => {
    if (accessGroupId) {
      if (!selectedAccessGroupDetail || accessGroupLoading) {
        return null;
      }
      if (!groupLockAliases.length) {
        return [];
      }
      if (lockId) {
        return intersectLockFilterAliases(groupLockAliases, lockAliases);
      }
      return groupLockAliases;
    }
    return lockAliases;
  }, [
    accessGroupId,
    selectedAccessGroupDetail,
    accessGroupLoading,
    groupLockAliases,
    lockId,
    lockAliases
  ]);

  const selectedLock = useMemo(() => {
    if (!lockId) {
      return null;
    }
    const needle = lockId.toLowerCase();
    return (
      locks.find((lock) =>
        [lock.lockId, lock.id, lock.externalId]
          .filter(Boolean)
          .some((id) => String(id).toLowerCase() === needle)
      ) || null
    );
  }, [lockId, locks]);

  const selectedAccessGroupOption = useMemo(() => {
    if (!accessGroupId) {
      return null;
    }
    return (
      accessGroups.find((group) => String(group.id) === String(accessGroupId)) ||
      (selectedAccessGroupDetail && String(selectedAccessGroupDetail.id) === String(accessGroupId)
        ? selectedAccessGroupDetail
        : null)
    );
  }, [accessGroupId, accessGroups, selectedAccessGroupDetail]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    if (key !== 'from' && !next.get('from')) {
      next.set('from', defaultFromIso());
    }
    if (key !== 'to' && !next.get('to')) {
      next.set('to', defaultToIso());
    }
    // Behold rapport-kontekst når hendelsesloggen ligger under Rapporter
    if (!next.get('report')) {
      next.set('report', 'hendelseslogg');
    }
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [lockList, phoneList, groups] = await Promise.all([
          dataverseService.lockService.getAll().catch(() => []),
          dataverseService.phoneService.getAll().catch(() => []),
          accessGroupService.getAll().catch(() => [])
        ]);
        if (!cancelled) {
          setLocks(lockList || []);
          setPhones(phoneList || []);
          setAccessGroups(groups || []);
        }
      } catch {
        if (!cancelled) {
          setLocks([]);
          setPhones([]);
          setAccessGroups([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!accessGroupId) {
      setSelectedAccessGroupDetail(null);
      setAccessGroupLoading(false);
      return undefined;
    }

    let cancelled = false;
    setAccessGroupLoading(true);
    (async () => {
      try {
        const detail = await accessGroupService.getById(accessGroupId);
        if (!cancelled) {
          setSelectedAccessGroupDetail(detail);
        }
      } catch (groupError) {
        if (!cancelled) {
          setSelectedAccessGroupDetail(null);
          setError(
            getDataverseErrorMessage(groupError, 'Kunne ikke hente tilgangsgruppens låser.')
          );
        }
      } finally {
        if (!cancelled) {
          setAccessGroupLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [accessGroupId]);

  // Strip GUI placeholder from URL so phone/lock logs are not emptied by user filter.
  useEffect(() => {
    if (isGuiPlaceholderUserId(user) && searchParams.get('user')) {
      const next = new URLSearchParams(searchParams);
      next.delete('user');
      if (!next.get('report')) {
        next.set('report', 'hendelseslogg');
      }
      setSearchParams(next, { replace: true });
    }
  }, [user, searchParams, setSearchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setUnlocWarning(null);
    setOndemandWarning(null);

    const sessionFrom = new Date(from);
    if (!Number.isNaN(sessionFrom.getTime())) {
      sessionFrom.setHours(sessionFrom.getHours() - 36);
    }

    let groupDetail = selectedAccessGroupDetail;
    if (accessGroupId && (!groupDetail || String(groupDetail.id) !== String(accessGroupId))) {
      try {
        groupDetail = await accessGroupService.getById(accessGroupId);
        setSelectedAccessGroupDetail(groupDetail);
      } catch (groupError) {
        setError(
          getDataverseErrorMessage(groupError, 'Kunne ikke hente tilgangsgruppens låser.')
        );
        setRows([]);
        setLoading(false);
        return;
      }
    }

    const groupAliases = accessGroupId
      ? collectLockFilterAliases(groupDetail?.locks || [], locks)
      : [];
    const singleAliases = resolveLockFilterAliases(lockId, locks);
    let queryLockIds = singleAliases;
    if (accessGroupId) {
      queryLockIds = lockId
        ? intersectLockFilterAliases(groupAliases, singleAliases)
        : groupAliases;
    }

    if (accessGroupId && queryLockIds.length === 0) {
      setRows([]);
      setFetchStats({ unloc: 0, ondemand: 0 });
      setUnlocWarning(
        lockId
          ? 'Valgt lås er ikke medlem av tilgangsgruppen.'
          : 'Tilgangsgruppen har ingen låser — ingen hendelser å vise.'
      );
      setLoading(false);
      return;
    }

    // Unloc API only accepts one lockId. With group-only filter, fetch broadly and filter client-side.
    const useSingleLockForUnloc = Boolean(lockId) && (!accessGroupId || queryLockIds.length > 0);
    const unlocLockId = useSingleLockForUnloc
      ? locks.find((lock) =>
          [lock.lockId, lock.id, lock.externalId]
            .filter(Boolean)
            .some((id) => String(id).toLowerCase() === String(lockId || '').toLowerCase())
        )?.externalId || singleAliases[0] || lockId || undefined
      : undefined;

    const skipUnloc = source === LOG_SOURCE.ON_DEMAND || source === 'ondemand';

    // Dataverse first — live Unloc access-events is ~20s for a full project page.
    // Webhook/backfill already stores Unloc rows in oc_opencarelog.
    const [opencareRecords, mobilLogs] = await Promise.all([
      dataverseService.opencareLogService
        .list({
          from,
          to: to || undefined,
          lockIds: queryLockIds.length ? queryLockIds : undefined,
          phone: phone || undefined
        })
        .catch((ondemandError) => {
          setOndemandWarning(
            getDataverseErrorMessage(
              ondemandError,
              'Kunne ikke hente on-demand-logg fra Dataverse.'
            )
          );
          return [];
        }),
      dataverseService.mobilLogService
        .list({
          from: sessionFrom.toISOString(),
          to: to || undefined,
          phone: phone || undefined
        })
        .catch(() => [])
    ]);

    const dvUnlocCount = (opencareRecords || []).filter((record) => {
      const id = String(record.oc_id || '');
      return (
        id.startsWith('unloc-') ||
        Number(record.oc_logsource) === OC_LOG_SOURCE.UNLOC
      );
    }).length;

    // Live Unloc only as fallback when DV has no stored Unloc events (small page — API is slow).
    let unlocResult = { events: [] };
    if (!skipUnloc && dvUnlocCount === 0) {
      const scoped = Boolean(unlocLockId || phone);
      unlocResult = await listUnlocAccessEvents({
        lockId: unlocLockId || undefined,
        appUserId: phone || undefined,
        from,
        to: to || undefined,
        limit: scoped ? 500 : 100,
        maxPages: 1
      }).catch((unlocError) => {
        setUnlocWarning(
          getDataverseErrorMessage(
            unlocError,
            'Kunne ikke hente Unloc-hendelseslogg. Viser on-demand-logg hvis tilgjengelig.'
          )
        );
        return { events: [] };
      });

      if (unlocResult === null) {
        setUnlocWarning(
          'Unloc-token mangler. Tillat tilgang til Unloc (popup) eller logg ut/inn, og oppdater. On-demand vises fortsatt.'
        );
      } else if (!(unlocResult.events || []).length && !phone && !lockId && !accessGroupId) {
        setUnlocWarning(
          'Ingen lagrede Unloc-hendelser i Dataverse, og live Unloc returnerte 0. Sjekk webhook/backfill eller Unloc-tilgang.'
        );
      }
    }

    try {
      const unlocEvents = unlocResult?.events || [];
      setFetchStats({
        unloc: unlocEvents.length || dvUnlocCount,
        ondemand: (opencareRecords || []).length
      });
      const merged = mergeAccessLogRows(unlocEvents, opencareRecords || []);
      const enriched = enrichLogsWithLocks(merged, locks);
      const sessions = buildPhoneSessions(mobilLogs || []);
      const withUsers = attachSessionUsers(enriched, sessions);
      const withPhones = enrichLogsWithPhones(withUsers, phones);
      setRows(linkOnDemandEpisodes(withPhones));
    } catch (loadError) {
      setError(getDataverseErrorMessage(loadError, 'Kunne ikke behandle hendelseslogg.'));
    } finally {
      setLoading(false);
    }
  }, [from, to, lockId, phone, source, locks, phones, accessGroupId, selectedAccessGroupDetail]);

  useEffect(() => {
    if (!autoLoad) {
      return undefined;
    }
    load();
    return undefined;
  }, [autoLoad, load]);

  useEffect(() => {
    if (autoLoad || runId == null || runId < 1) {
      return undefined;
    }
    // Only runId should trigger deferred load — not filter-driven load identity changes.
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: re-run on runId only
  }, [autoLoad, runId]);

  const filtered = useMemo(() => {
    if (effectiveLockFilterIds === null) {
      return [];
    }
    if (accessGroupId && effectiveLockFilterIds.length === 0) {
      return [];
    }
    return filterAccessLogs(rows, {
      source,
      lockId: accessGroupId ? '' : lockId,
      lockIds: effectiveLockFilterIds,
      phone,
      user,
      from,
      to: to || undefined,
      search
    });
  }, [
    rows,
    source,
    lockId,
    accessGroupId,
    effectiveLockFilterIds,
    phone,
    user,
    from,
    to,
    search
  ]);

  const handleExport = () => {
    const date = new Date().toISOString().slice(0, 10);
    downloadWorkbook(`opencare-hendelseslogg-${date}.xlsx`, [
      {
        name: 'Hendelseslogg',
        headers: ['Tid', 'Kilde', 'Handling', 'Lås', 'Lås-ID', 'Telefon', 'Bruker', 'Begrunnelse'],
        rows: filtered.map((row) => ({
          Tid: formatDateTime(row.timestamp),
          Kilde: logSourceLabel(row.source),
          Handling: logActionLabel(row.action),
          Lås: row.lockName,
          'Lås-ID': row.lockId,
          Telefon: row.phoneNumber,
          Bruker: sessionUserLabel(row),
          Begrunnelse: row.denialReason || ''
        }))
      }
    ]);
  };

  const titleBits = [];
  if (accessGroupId) {
    titleBits.push(
      selectedAccessGroupOption?.name
        ? `tilgangsgruppe «${selectedAccessGroupOption.name}»`
        : 'per tilgangsgruppe'
    );
  }
  if (lockId) {
    titleBits.push('per lås');
  }
  if (phone) {
    titleBits.push('per telefon');
  }
  if (user) {
    titleBits.push('per bruker');
  }

  return (
    <Box>
      {!hideChrome && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h4" component="h1" fontWeight={600}>
              Hendelseslogg
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Unloc åpne/låse (live API siste 60 dager + lagret historikk i Dataverse) og
              on-demand AccessControl-forespørsler
              {titleBits.length ? ` — ${titleBits.join(', ')}` : ''}
              {hasRun && !loading
                ? ` · hentet ${fetchStats.unloc} Unloc / ${fetchStats.ondemand} Dataverse`
                : ''}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={load}
              disabled={loading || !hasRun}
            >
              Oppdater
            </Button>
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={handleExport}
              disabled={!hasRun || !filtered.length}
            >
              Eksporter til Excel
            </Button>
          </Box>
        </Box>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Kilde</InputLabel>
            <Select
              label="Kilde"
              value={source}
              onChange={(event) => setParam('source', event.target.value === 'all' ? '' : event.target.value)}
            >
              <MenuItem value="all">Begge</MenuItem>
              <MenuItem value={LOG_SOURCE.UNLOC}>Unloc</MenuItem>
              <MenuItem value={LOG_SOURCE.ON_DEMAND}>On-demand</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Fra"
            type="datetime-local"
            value={toDatetimeLocalValue(from)}
            onChange={(event) => setParam('from', fromDatetimeLocalValue(event.target.value))}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            label="Til"
            type="datetime-local"
            value={toDatetimeLocalValue(to)}
            onChange={(event) => setParam('to', fromDatetimeLocalValue(event.target.value))}
            InputLabelProps={{ shrink: true }}
          />
          <Autocomplete
            size="small"
            sx={{ minWidth: 260 }}
            options={accessGroups}
            value={selectedAccessGroupOption}
            loading={accessGroupLoading}
            getOptionLabel={(group) => group?.name || String(group?.id || '')}
            isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
            onChange={(_event, group) => {
              setParam('accessGroup', group ? group.id : '');
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tilgangsgruppe"
                placeholder="Alle grupper…"
                helperText={
                  accessGroupId
                    ? 'Hendelser for låsene i gruppen (ikke kun gruppenøkler)'
                    : 'Valgfritt filter på gruppens låser'
                }
              />
            )}
          />
          <Autocomplete
            size="small"
            sx={{ minWidth: 280 }}
            options={locks}
            value={selectedLock}
            getOptionLabel={(lock) =>
              lock?.name
                ? `${lock.name}${lock.externalId ? ` (${lock.externalId})` : ''}`
                : String(lock?.lockId || '')
            }
            isOptionEqualToValue={(option, value) =>
              String(option.lockId || option.id) === String(value.lockId || value.id)
            }
            onChange={(_event, lock) => {
              setParam('lockId', lock ? lock.externalId || lock.lockId || '' : '');
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Lås"
                placeholder="Velg lås eller søk…"
                helperText={lockId && !selectedLock ? `Filter: ${lockId}` : 'Hendelseslogg per lås'}
              />
            )}
          />
          <TextField
            size="small"
            label="Lås-ID"
            value={lockId}
            onChange={(event) => setParam('lockId', event.target.value.trim())}
            sx={{ minWidth: 200 }}
            helperText="Unloc-ID eller Dataverse-ID"
          />
          <TextField
            size="small"
            label="Telefon"
            value={phone}
            onChange={(event) => setParam('phone', event.target.value.trim())}
            sx={{ minWidth: 160 }}
          />
          <TextField
            size="small"
            label="Pålogget bruker"
            value={isGuiPlaceholderUserId(user) ? '' : user}
            onChange={(event) => setParam('user', event.target.value.trim())}
            sx={{ minWidth: 180 }}
            helperText="Valgfritt — tomt viser alle hendelser"
          />
          <TextField
            size="small"
            label="Søk"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{ minWidth: 180, flex: 1 }}
          />
        </Box>
      </Paper>

      {!hasRun ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Juster filtrene over, og trykk <strong>Kjør rapport</strong> for å hente hendelsesloggen.
        </Alert>
      ) : null}

      {hasRun && error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {hasRun && unlocWarning && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setUnlocWarning(null)}>
          {unlocWarning}
        </Alert>
      )}
      {hasRun && ondemandWarning && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setOndemandWarning(null)}>
          {ondemandWarning}
        </Alert>
      )}

      {!hasRun ? null : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tid</TableCell>
                <TableCell>Kilde</TableCell>
                <TableCell>Handling</TableCell>
                <TableCell>Lås</TableCell>
                <TableCell>Telefon</TableCell>
                <TableCell>Pålogget bruker</TableCell>
                <TableCell>Begrunnelse</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography color="text.secondary" sx={{ mb: 1 }}>
                      Ingen hendelser i valgt periode
                      {rows.length > 0
                        ? ` (filter skjulte ${rows.length} hentede rader — fjern telefon/bruker/lås/tilgangsgruppe-filter).`
                        : '.'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Hentet: {fetchStats.unloc} Unloc, {fetchStats.ondemand} Dataverse
                      (lagret Unloc + on-demand). Live Unloc dekker siste 60 dager; eldre
                      hendelser kommer fra lagret historikk når webhook/backfill er aktiv.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{formatDateTime(row.timestamp)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={logSourceLabel(row.source)} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={
                          row.action === 'denied'
                            ? 'error'
                            : row.action === 'granted'
                              ? 'info'
                              : row.action === 'unlocked'
                                ? 'success'
                                : 'default'
                        }
                        label={logActionLabel(row.action)}
                      />
                      {row.episodeId && row.source === LOG_SOURCE.ON_DEMAND && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          Tilknyttet åpning
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        onClick={() =>
                          setParam('lockId', row.unlocLockId || row.lockId)
                        }
                        sx={{ textTransform: 'none', justifyContent: 'flex-start' }}
                      >
                        {row.lockName || row.lockId || '—'}
                      </Button>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {row.lockId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {row.phoneNumber ? (
                        <Button
                          size="small"
                          startIcon={<History fontSize="inherit" />}
                          sx={{ textTransform: 'none' }}
                          onClick={() => setParam('phone', normalizeMobileNumber(row.phoneNumber) || row.phoneNumber)}
                        >
                          {row.phoneNumber}
                        </Button>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      {sessionUserLabel(row) ? (
                        <Button
                          size="small"
                          sx={{ textTransform: 'none' }}
                          onClick={() =>
                            setParam('user', row.userId || row.userEmail || row.userName || row.unlocUserName)
                          }
                        >
                          {sessionUserLabel(row)}
                        </Button>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{row.denialReason || '—'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
}
